import React from "react";
import { FiTrash2, FiAlertCircle } from "react-icons/fi";

export interface BlockedPeriod {
  id?: number;
  start_date: string;
  end_date: string;
  reason?: string;
  is_active?: boolean;
  duration_days?: number;
}

interface BlockedPeriodItemProps {
  period: BlockedPeriod;
  index: number;
  error?: string;
  onChange: (index: number, field: keyof BlockedPeriod, value: string) => void;
  onRemove: (index: number) => void;
  getDurationDays: (startDate: string, endDate: string) => number;
}

export default function BlockedPeriodItem({
  period,
  index,
  error,
  onChange,
  onRemove,
  getDurationDays,
}: BlockedPeriodItemProps) {
  return (
    <div
      className={`rounded-lg border bg-white p-4 shadow-sm transition dark:bg-boxdark ${
        error
          ? "border-red-300 dark:border-red-800"
          : "border-stroke dark:border-strokedark"
      }`}
    >
      <div className="grid grid-cols-1 gap-4">
        {/* Primeira linha: Datas */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-6 lg:items-start">
          {/* Data Início */}
          <div className="lg:col-span-3">
            <label className="mb-2 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Data Início
            </label>
            <input
              type="date"
              value={period.start_date}
              onChange={(e) => onChange(index, "start_date", e.target.value)}
              className="w-full rounded-lg border border-stroke bg-transparent px-3 py-2.5 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:ring-orange-800"
            />
          </div>

          {/* Data Fim */}
          <div className="lg:col-span-3">
            <label className="mb-2 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Data Fim
            </label>
            <input
              type="date"
              value={period.end_date}
              onChange={(e) => onChange(index, "end_date", e.target.value)}
              className="w-full rounded-lg border border-stroke bg-transparent px-3 py-2.5 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:ring-orange-800"
            />
          </div>
        </div>

        {/* Segunda linha: Motivo e Duração */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-start">
          {/* Motivo */}
          <div className="lg:col-span-8">
            <label className="mb-2 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Motivo (opcional)
            </label>
            <input
              type="text"
              value={period.reason || ""}
              onChange={(e) => onChange(index, "reason", e.target.value)}
              placeholder="Ex: Férias, Feriado, etc"
              className="w-full rounded-lg border border-stroke bg-transparent px-3 py-2.5 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:ring-orange-800"
            />
          </div>

          {/* Duração e Ações */}
          <div className="lg:col-span-4">
            <label className="mb-2 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Duração
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-lg border border-stroke bg-gray-50 px-3 py-2.5 text-sm text-gray-700 dark:border-strokedark dark:bg-gray-800 dark:text-gray-300">
                {getDurationDays(period.start_date, period.end_date)} dia(s)
              </div>
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="rounded-lg border border-red-300 bg-red-50 p-2.5 text-red-700 transition hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                title="Remover período"
              >
                <FiTrash2 size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
          <FiAlertCircle
            className="mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400"
            size={16}
          />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  );
}
